import React, { useState } from 'react';
import { Alert, Modal } from 'antd';
import { t } from 'ttag';
import styled from 'styled-components';
import ReactCrop, {
  PercentCrop,
  PixelCrop,
  centerCrop,
  clamp,
  convertToPercentCrop,
  convertToPixelCrop,
  makeAspectCrop,
} from 'react-image-crop';

import 'react-image-crop/dist/ReactCrop.css';

import Loading from '../Loading';

import { checkeredBackground } from '../../styles';

/** Crop geometry in natural image pixels. API crop data carries no unit, so none is accepted. */
export type InitialCrop = Omit<PixelCrop, 'unit'>;

export interface ImageCropProps {
  src: string;
  onCropSaveClick: (result: PixelCrop) => void;
  /** Initial crop selection, in natural image pixels. Defaults to a centered crop. */
  initialCrop?: InitialCrop;
  aspect?: number;
  /** Selection bounds, in natural image pixels. */
  maxHeight?: number;
  minHeight?: number;
  maxWidth?: number;
  minWidth?: number;
  open: boolean;
  maskClosable?: boolean;
  onModalClose: () => void;
  circularCrop?: boolean;
}

interface LoadedImage {
  src: string;
  width: number;
  height: number;
  naturalWidth: number;
  naturalHeight: number;
}

// A missing src fires no error event, so the URL is vetted before the <img> renders.
const URL_PROTOCOL_REGEXP = /^https?:\/\//;

const StyledReactCrop = styled(ReactCrop)`
  && {
    ${checkeredBackground}
  }
`;

// Public bounds are in natural image pixels; ReactCrop expects displayed pixels.
const toDisplayedPx = (bound: number | undefined, natural: number, displayed: number) =>
  bound ? (bound * displayed) / natural : undefined;

type InitialPercentCropOptions = Pick<
  ImageCropProps,
  'initialCrop' | 'aspect' | 'minWidth' | 'minHeight' | 'maxWidth' | 'maxHeight'
> & {
  naturalWidth: number;
  naturalHeight: number;
};

const initialPercentCrop = ({
  initialCrop,
  aspect,
  minWidth,
  minHeight,
  maxWidth,
  maxHeight,
  naturalWidth,
  naturalHeight,
}: InitialPercentCropOptions): PercentCrop => {
  // Partial geometry is treated as no crop — better the largest default than a made-up sliver.
  if (initialCrop?.width && initialCrop?.height) {
    const width = clamp(initialCrop.width, minWidth ?? 0, naturalWidth);
    const height = clamp(initialCrop.height, minHeight ?? 0, naturalHeight);

    return convertToPercentCrop(
      {
        unit: 'px',
        width,
        height,
        x: clamp(initialCrop.x || 0, 0, naturalWidth - width),
        y: clamp(initialCrop.y || 0, 0, naturalHeight - height),
      },
      naturalWidth,
      naturalHeight,
    );
  }

  // No crop given: select the largest centered area that fits the aspect and max bounds.
  // makeAspectCrop clamps to its container, so the max bounds act as the container here
  // and centerCrop then centers the result within the actual image.
  const boundedWidth = Math.min(naturalWidth, maxWidth || naturalWidth);
  const boundedHeight = Math.min(naturalHeight, maxHeight || naturalHeight);
  const largest: PixelCrop = aspect
    ? makeAspectCrop({ unit: 'px', width: boundedWidth }, aspect, boundedWidth, boundedHeight)
    : { unit: 'px', x: 0, y: 0, width: boundedWidth, height: boundedHeight };

  return convertToPercentCrop(
    centerCrop(largest, naturalWidth, naturalHeight),
    naturalWidth,
    naturalHeight,
  );
};

const ImageCrop = ({
  src,
  onCropSaveClick,
  initialCrop = undefined,
  aspect = undefined,
  maxHeight = undefined,
  minHeight = undefined,
  maxWidth = undefined,
  minWidth = undefined,
  open,
  onModalClose,
  maskClosable = false,
  circularCrop = false,
}: ImageCropProps) => {
  const [loadedImage, setLoadedImage] = useState<LoadedImage | null>(null);
  const [failedSrc, setFailedSrc] = useState<string | null>(null);
  const [crop, setCrop] = useState<PercentCrop | null>(null);

  // Invalidated during render, so no reset effect is needed when src changes.
  const image = loadedImage?.src === src ? loadedImage : null;
  const failed = !URL_PROTOCOL_REGEXP.test(src) || failedSrc === src;

  // A circular selection with a non-square aspect renders as an oval; force the circle.
  const cropAspect = circularCrop ? 1 : aspect;

  const onImageLoad = (event: React.SyntheticEvent<HTMLImageElement>) => {
    const { width, height, naturalWidth, naturalHeight } = event.currentTarget;

    setLoadedImage({ src, width, height, naturalWidth, naturalHeight });
    setCrop(
      initialPercentCrop({
        initialCrop,
        aspect: cropAspect,
        minWidth,
        minHeight,
        maxWidth,
        maxHeight,
        naturalWidth,
        naturalHeight,
      }),
    );
  };

  const onOk = () => {
    if (!crop || !image) {
      return;
    }

    onCropSaveClick(convertToPixelCrop(crop, image.naturalWidth, image.naturalHeight));
  };

  return (
    <Modal
      title={t`Crop Image`}
      centered
      closable={false}
      mask={{ closable: maskClosable }}
      open={open}
      onCancel={onModalClose}
      onOk={onOk}
      okText={t`Crop & Save Image`}
      okButtonProps={{ disabled: !image || !crop }}
      cancelText={t`Close`}
    >
      {failed ? (
        <Alert title={t`Failed to load image.`} type="error" showIcon />
      ) : (
        <StyledReactCrop
          crop={image && crop ? crop : undefined}
          aspect={cropAspect}
          circularCrop={circularCrop}
          minWidth={image ? toDisplayedPx(minWidth, image.naturalWidth, image.width) : undefined}
          minHeight={
            image ? toDisplayedPx(minHeight, image.naturalHeight, image.height) : undefined
          }
          maxWidth={image ? toDisplayedPx(maxWidth, image.naturalWidth, image.width) : undefined}
          maxHeight={
            image ? toDisplayedPx(maxHeight, image.naturalHeight, image.height) : undefined
          }
          onChange={(_pixelCrop, percentCrop) => setCrop(percentCrop)}
        >
          <img
            src={src}
            alt={t`Crop source`}
            onLoad={onImageLoad}
            onError={() => setFailedSrc(src)}
          />
        </StyledReactCrop>
      )}
      {!image && !failed && <Loading />}
    </Modal>
  );
};

export default ImageCrop;
